import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Box, Button, Grid, TextField, Paper, Typography,
  Autocomplete, Tabs, Tab, Chip, Stack, Card, CardContent, Alert, Divider
} from '@mui/material';
import {
  Dashboard as DashboardIcon, AttachMoney as MoneyIcon,
  DirectionsCar as CarIcon, People as PeopleIcon, Warning as WarningIcon,
  TrendingUp as TrendingIcon, CalendarToday as CalendarIcon,
  Payment as PaymentIcon, Assessment as ChartIcon, Speed as SpeedIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Chart from 'chart.js/auto';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_API_URL;

export default function Relatorios() {
  const [alugueis, setAlugueis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [filtros, setFiltros] = useState({
    data_inicial: '',
    data_final: '',
    cliente: null,
    veiculo: null
  });

  // Carregar dados iniciais
  useEffect(() => {
    carregarClientes();
    carregarVeiculos();
    carregarAlugueis();
    // eslint-disable-next-line
  }, []);

  // Recarregar quando filtros mudarem
  useEffect(() => {
    carregarAlugueis();
    // eslint-disable-next-line
  }, [filtros]);

  // Recarregar quando mudar de aba
  useEffect(() => {
    if (alugueis.length > 0) {
      // Pequeno delay para garantir que os gráficos renderizem corretamente
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [tabIndex, alugueis]);

  const carregarClientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/clientes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientes(data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const carregarVeiculos = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/veiculos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVeiculos(data);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
    }
  };

  const carregarAlugueis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filtros.data_inicial) params.data_inicial = filtros.data_inicial;
      if (filtros.data_final) params.data_final = filtros.data_final;
      if (filtros.cliente?.id) params.id_cliente = filtros.cliente.id;
      if (filtros.veiculo?.id) params.id_veiculo = filtros.veiculo.id;

      const { data } = await axios.get(`${API_URL}/relatorios/alugueis`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlugueis(data);
    } catch (error) {
      console.error('Erro ao carregar aluguéis:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cálculos gerais
  const stats = useMemo(() => {
    const totalAlugueis = alugueis.length;
    const receitaTotal = alugueis.reduce((acc, a) => acc + parseFloat(a.valor_total || 0), 0);
    const descontosTotal = alugueis.reduce((acc, a) => acc + parseFloat(a.desconto_valor || 0), 0);
    const receitaLiquida = receitaTotal - descontosTotal;
    const ticketMedio = totalAlugueis > 0 ? receitaTotal / totalAlugueis : 0;
    
    const totalDias = alugueis.reduce((acc, a) => {
      if (a.data_retirada && a.data_devolucao) {
        const dias = Math.ceil((new Date(a.data_devolucao) - new Date(a.data_retirada)) / (1000 * 60 * 60 * 24));
        return acc + Math.max(1, dias);
      }
      return acc;
    }, 0);
    const mediaDias = totalAlugueis > 0 ? totalDias / totalAlugueis : 0;

    return {
      totalAlugueis,
      receitaTotal,
      descontosTotal,
      receitaLiquida,
      ticketMedio,
      mediaDias
    };
  }, [alugueis]);

  // Análise de veículos
  const analiseVeiculos = useMemo(() => {
    const porVeiculo = {};
    alugueis.forEach(a => {
      const key = a.placa || 'Sem Placa';
      if (!porVeiculo[key]) {
        porVeiculo[key] = {
          placa: key,
          modelo: a.modelo || '',
          totalAlugueis: 0,
          receita: 0
        };
      }
      porVeiculo[key].totalAlugueis++;
      porVeiculo[key].receita += parseFloat(a.valor_total || 0);
    });
    return Object.values(porVeiculo).sort((a, b) => b.totalAlugueis - a.totalAlugueis);
  }, [alugueis]);

  // Análise de clientes
  const analiseClientes = useMemo(() => {
    const porCliente = {};
    alugueis.forEach(a => {
      const key = a.nome_cliente || 'Sem Nome';
      if (!porCliente[key]) {
        porCliente[key] = {
          nome: key,
          totalAlugueis: 0,
          receita: 0
        };
      }
      porCliente[key].totalAlugueis++;
      porCliente[key].receita += parseFloat(a.valor_total || 0);
    });
    return Object.values(porCliente).sort((a, b) => b.receita - a.totalAlugueis);
  }, [alugueis]);

  // Alertas
  const alertas = useMemo(() => {
    const dataLimite30 = new Date();
    dataLimite30.setDate(dataLimite30.getDate() - 30);

    const abertosMais30 = alugueis.filter(a => {
      const dataRetirada = new Date(a.data_retirada);
      const estaAberto = ['Reservado', 'Em Andamento', 'Ativo', 'Agendado', 'Pendente'].includes(a.status_aluguel);
      const pendente = a.status_pagamento === 'Pendente';
      return estaAberto && pendente && dataRetirada < dataLimite30;
    });

    const atrasados = alugueis.filter(a => a.dias_atraso > 0);
    const pendentes = alugueis.filter(a => a.status_pagamento === 'Pendente');

    return {
      abertosMais30,
      atrasados,
      pendentes
    };
  }, [alugueis]);

  // Gráficos
  useEffect(() => {
    if (alugueis.length === 0) return;

    // Gráfico 1: Aluguéis por mês
    const ctx1 = document.getElementById('grafico-timeline');
    if (ctx1) {
      const existente = Chart.getChart(ctx1);
      if (existente) existente.destroy();

      const porMes = {};
      alugueis.forEach(a => {
        const mes = new Date(a.data_retirada).toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' });
        porMes[mes] = (porMes[mes] || 0) + 1;
      });

      new Chart(ctx1, {
        type: 'line',
        data: {
          labels: Object.keys(porMes),
          datasets: [{
            label: 'Aluguéis',
            data: Object.values(porMes),
            borderColor: '#1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            tension: 0.4,
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true } }
        }
      });
    }

    // Gráfico 2: Formas de pagamento
    const ctx2 = document.getElementById('grafico-pagamento');
    if (ctx2) {
      const existente = Chart.getChart(ctx2);
      if (existente) existente.destroy();

      const porForma = {};
      alugueis.forEach(a => {
        const forma = a.forma_pagamento || 'Não definido';
        porForma[forma] = (porForma[forma] || 0) + 1;
      });

      new Chart(ctx2, {
        type: 'doughnut',
        data: {
          labels: Object.keys(porForma),
          datasets: [{
            data: Object.values(porForma),
            backgroundColor: ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0']
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right' }
          }
        }
      });
    }

    // Gráfico 3: Top veículos
    const ctx3 = document.getElementById('grafico-veiculos');
    if (ctx3) {
      const existente = Chart.getChart(ctx3);
      if (existente) existente.destroy();

      const top10 = analiseVeiculos.slice(0, 10);

      new Chart(ctx3, {
        type: 'bar',
        data: {
          labels: top10.map(v => v.placa),
          datasets: [{
            label: 'Aluguéis',
            data: top10.map(v => v.totalAlugueis),
            backgroundColor: '#4caf50'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true } }
        }
      });
    }

    // Gráfico 4: Top clientes
    const ctx4 = document.getElementById('grafico-clientes');
    if (ctx4) {
      const existente = Chart.getChart(ctx4);
      if (existente) existente.destroy();

      const top10 = analiseClientes.slice(0, 10);

      new Chart(ctx4, {
        type: 'bar',
        data: {
          labels: top10.map(c => c.nome.substring(0, 15)),
          datasets: [{
            label: 'Aluguéis',
            data: top10.map(c => c.totalAlugueis),
            backgroundColor: '#ff9800'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: { legend: { display: false } },
          scales: { x: { beginAtZero: true } }
        }
      });
    }
  }, [alugueis, analiseVeiculos, analiseClientes]);

  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const exportarPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const dataAtual = new Date().toLocaleString('pt-BR');
    
    // Nome da aba atual
    const abas = ['Visão Geral', 'Financeiro', 'Veículos', 'Clientes', 'Alertas'];
    const nomeAba = abas[tabIndex];

    // ===== CABEÇALHO =====
    let yPos = 15;
    
    // Título principal
    doc.setFillColor(31, 31, 31); // #1f1f1f
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    doc.setTextColor(247, 139, 0); // #f78b00
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('EASY LOCADORA', pageWidth / 2, 12, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text(`RELATÓRIO DE ALUGUÉIS - ${nomeAba.toUpperCase()}`, pageWidth / 2, 20, { align: 'center' });

    yPos = 32;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em: ${dataAtual}`, pageWidth - 15, yPos, { align: 'right' });

    yPos = 40;

    // ===== FILTROS APLICADOS =====
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(247, 139, 0);
    doc.text('FILTROS APLICADOS:', 15, yPos);
    
    yPos += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    const periodo = filtros.data_inicial && filtros.data_final 
      ? `${formatarData(filtros.data_inicial)} a ${formatarData(filtros.data_final)}`
      : 'Todos os períodos';
    doc.text(`• Período: ${periodo}`, 15, yPos);
    
    yPos += 5;
    const veiculoFiltro = filtros.veiculo 
      ? `${filtros.veiculo.placa} - ${filtros.veiculo.modelo}`
      : 'Todos os veículos';
    doc.text(`• Veículo: ${veiculoFiltro}`, 15, yPos);
    
    yPos += 5;
    const clienteFiltro = filtros.cliente 
      ? filtros.cliente.nome
      : 'Todos os clientes';
    doc.text(`• Cliente: ${clienteFiltro}`, 15, yPos);

    yPos += 10;

    // ===== RESUMO ESTATÍSTICO =====
    doc.setFillColor(247, 139, 0);
    doc.rect(15, yPos - 5, pageWidth - 30, 8, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('RESUMO ESTATÍSTICO', pageWidth / 2, yPos, { align: 'center' });

    yPos += 10;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');

    // Cards de resumo em colunas
    const col1X = 15;
    const col2X = pageWidth / 2 + 10;
    let col1Y = yPos;
    let col2Y = yPos;

    // Coluna 1
    doc.setFont('helvetica', 'bold');
    doc.text('Total de Aluguéis:', col1X, col1Y);
    doc.setFont('helvetica', 'normal');
    doc.text(stats.totalAlugueis.toString(), col1X + 40, col1Y);
    col1Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Média de Dias:', col1X, col1Y);
    doc.setFont('helvetica', 'normal');
    doc.text(`${stats.mediaDias.toFixed(1)} dias`, col1X + 40, col1Y);
    col1Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Faturamento Bruto:', col1X, col1Y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarValor(stats.receitaTotal), col1X + 40, col1Y);
    col1Y += 5;

    // Coluna 2
    doc.setFont('helvetica', 'bold');
    doc.text('Descontos Concedidos:', col2X, col2Y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarValor(stats.descontosTotal), col2X + 45, col2Y);
    col2Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Receita Líquida:', col2X, col2Y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarValor(stats.receitaLiquida), col2X + 45, col2Y);
    col2Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Ticket Médio:', col2X, col2Y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarValor(stats.ticketMedio), col2X + 45, col2Y);

    yPos = Math.max(col1Y, col2Y) + 8;

    // Top performers (lado a lado)
    if (analiseVeiculos.length > 0 || analiseClientes.length > 0) {
      const topCol1X = 15;
      const topCol2X = pageWidth / 2 + 10;

      if (analiseVeiculos.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Veiculo Mais Alugado:', topCol1X, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(`${analiseVeiculos[0].placa} (${analiseVeiculos[0].totalAlugueis}x)`, topCol1X + 50, yPos);
      }

      if (analiseClientes.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Cliente Top:', topCol2X, yPos);
        doc.setFont('helvetica', 'normal');
        const nomeCliente = analiseClientes[0].nome.substring(0, 30);
        doc.text(`${nomeCliente} (${analiseClientes[0].totalAlugueis}x)`, topCol2X + 30, yPos);
      }

      yPos += 8;
    }

    // ===== TABELA DE DADOS =====
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(247, 139, 0);
    doc.text('DADOS DOS ALUGUÉIS:', 15, yPos);
    yPos += 2;

    // Preparar dados da tabela baseado na aba
    let tableColumns = [];
    let tableData = [];

    if (tabIndex === 0) {
      // Visão Geral
      tableColumns = ['Cliente', 'Placa', 'Modelo', 'Retirada', 'Devolução', 'Status', 'Valor'];
      tableData = dadosTabela.slice(0, 50).map(a => [
        a.nome_cliente?.substring(0, 25) || '',
        a.placa || '',
        a.modelo?.substring(0, 20) || '',
        formatarData(a.data_retirada),
        formatarData(a.data_devolucao),
        a.status_aluguel,
        formatarValor(a.valor_total)
      ]);
    } else if (tabIndex === 1) {
      // Financeiro
      tableColumns = ['Cliente', 'Placa', 'Retirada', 'Diária', 'Total', 'Desconto', 'Pgto', 'Forma'];
      tableData = dadosTabela.slice(0, 50).map(a => [
        a.nome_cliente?.substring(0, 20) || '',
        a.placa || '',
        formatarData(a.data_retirada),
        formatarValor(a.valor_diaria),
        formatarValor(a.valor_total),
        formatarValor(a.desconto_valor),
        a.status_pagamento,
        a.forma_pagamento?.substring(0, 12) || ''
      ]);
    } else if (tabIndex === 2) {
      // Veículos
      tableColumns = ['Placa', 'Modelo', 'Cliente', 'Retirada', 'Devolução', 'Valor'];
      tableData = dadosTabela.slice(0, 50).map(a => [
        a.placa || '',
        a.modelo?.substring(0, 25) || '',
        a.nome_cliente?.substring(0, 25) || '',
        formatarData(a.data_retirada),
        formatarData(a.data_devolucao),
        formatarValor(a.valor_total)
      ]);
    } else if (tabIndex === 3) {
      // Clientes
      tableColumns = ['Cliente', 'Placa', 'Modelo', 'Retirada', 'Valor', 'Pgto'];
      tableData = dadosTabela.slice(0, 50).map(a => [
        a.nome_cliente?.substring(0, 30) || '',
        a.placa || '',
        a.modelo?.substring(0, 20) || '',
        formatarData(a.data_retirada),
        formatarValor(a.valor_total),
        a.status_pagamento
      ]);
    } else {
      // Alertas
      tableColumns = ['Cliente', 'Placa', 'Retirada', 'Devolução', 'Atraso', 'Status', 'Valor'];
      tableData = dadosTabela.slice(0, 50).map(a => [
        a.nome_cliente?.substring(0, 25) || '',
        a.placa || '',
        formatarData(a.data_retirada),
        formatarData(a.data_devolucao),
        `${a.dias_atraso || 0}d`,
        a.status_aluguel,
        formatarValor(a.valor_total)
      ]);
    }

    // Gerar tabela
    autoTable(doc, {
      startY: yPos,
      head: [tableColumns],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [247, 139, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 15, right: 15 },
      tableWidth: 'auto'
    });

    // Rodapé
    const finalY = doc.lastAutoTable.finalY || yPos + 50;
    if (finalY < pageHeight - 15) {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `Relatório gerado automaticamente pelo sistema Easy Locadora - ${dataAtual}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Salvar
    const dataArquivo = new Date().toISOString().split('T')[0];
    const nomeArquivo = `relatorio-alugueis-${nomeAba.toLowerCase().replace(/\s+/g, '-')}-${dataArquivo}.pdf`;
    doc.save(nomeArquivo);
  };

  const exportarExcel = () => {
    const dataAtual = new Date().toLocaleString('pt-BR');
    const abas = ['Visão Geral', 'Financeiro', 'Veículos', 'Clientes', 'Alertas'];
    const nomeAba = abas[tabIndex];

    // ===== CABEÇALHO =====
    const cabecalho = [
      ['EASY LOCADORA'],
      [`RELATÓRIO DE ALUGUÉIS - ${nomeAba.toUpperCase()}`],
      [`Gerado em: ${dataAtual}`],
      [''], // Linha vazia
    ];

    // ===== FILTROS =====
    const periodo = filtros.data_inicial && filtros.data_final 
      ? `${formatarData(filtros.data_inicial)} a ${formatarData(filtros.data_final)}`
      : 'Todos os períodos';
    const veiculoFiltro = filtros.veiculo 
      ? `${filtros.veiculo.placa} - ${filtros.veiculo.modelo}`
      : 'Todos os veículos';
    const clienteFiltro = filtros.cliente 
      ? filtros.cliente.nome
      : 'Todos os clientes';

    const filtrosAplicados = [
      ['FILTROS APLICADOS:'],
      [`Período: ${periodo}`],
      [`Veículo: ${veiculoFiltro}`],
      [`Cliente: ${clienteFiltro}`],
      [''], // Linha vazia
    ];

    // ===== RESUMO ESTATÍSTICO =====
    const resumo = [
      ['RESUMO ESTATÍSTICO'],
      ['Total de Aluguéis:', stats.totalAlugueis],
      ['Média de Dias:', `${stats.mediaDias.toFixed(1)} dias`],
      ['Faturamento Bruto:', formatarValor(stats.receitaTotal)],
      ['Descontos Concedidos:', formatarValor(stats.descontosTotal)],
      ['Receita Líquida:', formatarValor(stats.receitaLiquida)],
      ['Ticket Médio:', formatarValor(stats.ticketMedio)],
    ];

    // Top performers
    if (analiseVeiculos.length > 0) {
      resumo.push(['Veículo Mais Alugado:', `${analiseVeiculos[0].placa} (${analiseVeiculos[0].totalAlugueis}x)`]);
    }
    if (analiseClientes.length > 0) {
      resumo.push(['Cliente Top:', `${analiseClientes[0].nome.substring(0, 30)} (${analiseClientes[0].totalAlugueis}x)`]);
    }
    resumo.push(['']); // Linha vazia

    // ===== DADOS DA TABELA =====
    let dadosHeader = [];
    let dadosBody = [];

    if (tabIndex === 0) {
      // Visão Geral
      dadosHeader = ['Cliente', 'Placa', 'Modelo', 'Retirada', 'Devolução', 'Status Aluguel', 'Valor Total'];
      dadosBody = dadosTabela.map(a => [
        a.nome_cliente || '',
        a.placa || '',
        a.modelo || '',
        formatarData(a.data_retirada),
        formatarData(a.data_devolucao),
        a.status_aluguel,
        formatarValor(a.valor_total)
      ]);
    } else if (tabIndex === 1) {
      // Financeiro
      dadosHeader = ['Cliente', 'Placa', 'Retirada', 'Valor Diária', 'Valor Total', 'Desconto', 'Status Pgto', 'Forma Pagamento'];
      dadosBody = dadosTabela.map(a => [
        a.nome_cliente || '',
        a.placa || '',
        formatarData(a.data_retirada),
        formatarValor(a.valor_diaria),
        formatarValor(a.valor_total),
        formatarValor(a.desconto_valor),
        a.status_pagamento,
        a.forma_pagamento || ''
      ]);
    } else if (tabIndex === 2) {
      // Veículos
      dadosHeader = ['Placa', 'Modelo', 'Cliente', 'Retirada', 'Devolução', 'Valor'];
      dadosBody = dadosTabela.map(a => [
        a.placa || '',
        a.modelo || '',
        a.nome_cliente || '',
        formatarData(a.data_retirada),
        formatarData(a.data_devolucao),
        formatarValor(a.valor_total)
      ]);
    } else if (tabIndex === 3) {
      // Clientes
      dadosHeader = ['Cliente', 'Placa', 'Modelo', 'Retirada', 'Valor', 'Status Pgto'];
      dadosBody = dadosTabela.map(a => [
        a.nome_cliente || '',
        a.placa || '',
        a.modelo || '',
        formatarData(a.data_retirada),
        formatarValor(a.valor_total),
        a.status_pagamento
      ]);
    } else {
      // Alertas
      dadosHeader = ['Cliente', 'Placa', 'Retirada', 'Devolução', 'Dias Atraso', 'Status', 'Valor'];
      dadosBody = dadosTabela.map(a => [
        a.nome_cliente || '',
        a.placa || '',
        formatarData(a.data_retirada),
        formatarData(a.data_devolucao),
        `${a.dias_atraso || 0} dias`,
        a.status_aluguel,
        formatarValor(a.valor_total)
      ]);
    }

    const tabelaDados = [
      ['DADOS DOS ALUGUÉIS:'],
      dadosHeader,
      ...dadosBody
    ];

    // ===== MONTAGEM FINAL =====
    const dadosCompletos = [
      ...cabecalho,
      ...filtrosAplicados,
      ...resumo,
      ...tabelaDados
    ];

    // Criar worksheet
    const ws = XLSX.utils.aoa_to_sheet(dadosCompletos);

    // Definir larguras das colunas
    const colWidths = dadosHeader.map((_, idx) => {
      const maxLength = Math.max(
        dadosHeader[idx]?.length || 10,
        ...dadosBody.map(row => (row[idx]?.toString() || '').length)
      );
      return { wch: Math.min(maxLength + 2, 40) };
    });
    ws['!cols'] = colWidths;

    // Criar workbook e adicionar worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, nomeAba);

    // Salvar arquivo
    const dataArquivo = new Date().toISOString().split('T')[0];
    const nomeArquivo = `relatorio-alugueis-${nomeAba.toLowerCase().replace(/\s+/g, '-')}-${dataArquivo}.xlsx`;
    const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buffer]), nomeArquivo);
  };

  const limparFiltros = () => {
    setFiltros({ data_inicial: '', data_final: '', cliente: null, veiculo: null });
  };

  // Colunas da tabela por aba
  const colunas = useMemo(() => {
    const base = [
      { accessorKey: 'nome_cliente', header: 'Cliente', size: 150 },
      { accessorKey: 'placa', header: 'Placa', size: 100 },
      { accessorKey: 'modelo', header: 'Modelo', size: 130 },
      { accessorKey: 'data_retirada', header: 'Retirada', Cell: ({ cell }) => formatarData(cell.getValue()), size: 100 },
      { accessorKey: 'data_devolucao', header: 'Devolução', Cell: ({ cell }) => formatarData(cell.getValue()), size: 100 }
    ];

    if (tabIndex === 0) {
      // Visão Geral
      return [
        ...base,
        { 
          accessorKey: 'status_aluguel', 
          header: 'Status',
          Cell: ({ cell }) => <Chip label={cell.getValue()} size="small" color="info" />,
          size: 110
        },
        { accessorKey: 'valor_total', header: 'Valor', Cell: ({ cell }) => formatarValor(cell.getValue()), size: 110 }
      ];
    } else if (tabIndex === 1) {
      // Financeiro
      return [
        ...base,
        { accessorKey: 'valor_diaria', header: 'Diária', Cell: ({ cell }) => formatarValor(cell.getValue()), size: 100 },
        { accessorKey: 'valor_total', header: 'Total', Cell: ({ cell }) => formatarValor(cell.getValue()), size: 110 },
        { accessorKey: 'desconto_valor', header: 'Desconto', Cell: ({ cell }) => formatarValor(cell.getValue()), size: 110 },
        { 
          accessorKey: 'status_pagamento', 
          header: 'Pgto',
          Cell: ({ cell }) => {
            const status = cell.getValue();
            return <Chip label={status} size="small" color={status === 'Pago' ? 'success' : 'warning'} />;
          },
          size: 100
        },
        { accessorKey: 'forma_pagamento', header: 'Forma', size: 120 }
      ];
    } else if (tabIndex === 2) {
      // Veículos
      return [
        { accessorKey: 'placa', header: 'Placa', size: 100 },
        { accessorKey: 'modelo', header: 'Modelo', size: 150 },
        { accessorKey: 'nome_cliente', header: 'Cliente', size: 150 },
        { accessorKey: 'data_retirada', header: 'Retirada', Cell: ({ cell }) => formatarData(cell.getValue()), size: 100 },
        { accessorKey: 'data_devolucao', header: 'Devolução', Cell: ({ cell }) => formatarData(cell.getValue()), size: 100 },
        { accessorKey: 'valor_total', header: 'Valor', Cell: ({ cell }) => formatarValor(cell.getValue()), size: 110 }
      ];
    } else if (tabIndex === 3) {
      // Clientes
      return [
        { accessorKey: 'nome_cliente', header: 'Cliente', size: 200 },
        { accessorKey: 'placa', header: 'Placa', size: 100 },
        { accessorKey: 'modelo', header: 'Modelo', size: 130 },
        { accessorKey: 'data_retirada', header: 'Retirada', Cell: ({ cell }) => formatarData(cell.getValue()), size: 100 },
        { accessorKey: 'valor_total', header: 'Valor', Cell: ({ cell }) => formatarValor(cell.getValue()), size: 110 },
        { 
          accessorKey: 'status_pagamento', 
          header: 'Pgto',
          Cell: ({ cell }) => {
            const status = cell.getValue();
            return <Chip label={status} size="small" color={status === 'Pago' ? 'success' : 'warning'} />;
          },
          size: 100
        }
      ];
    } else {
      // Alertas
      return [
        ...base,
        { 
          accessorKey: 'dias_atraso', 
          header: 'Atraso', 
          Cell: ({ cell }) => {
            const dias = cell.getValue() || 0;
            return dias > 0 ? <Chip label={`${dias}d`} color="error" size="small" /> : '-';
          },
          size: 80
        },
        { 
          accessorKey: 'status_aluguel', 
          header: 'Status',
          Cell: ({ cell }) => <Chip label={cell.getValue()} size="small" color="warning" />,
          size: 110
        },
        { accessorKey: 'valor_total', header: 'Valor', Cell: ({ cell }) => formatarValor(cell.getValue()), size: 110 }
      ];
    }
  }, [tabIndex]);

  const dadosTabela = useMemo(() => {
    if (tabIndex === 4) {
      // Alertas - mostrar apenas problemas
      return [...alertas.abertosMais30, ...alertas.atrasados];
    }
    return alugueis;
  }, [tabIndex, alugueis, alertas]);

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4, px: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <ChartIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Dashboard de Análises
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Relatórios completos e inteligentes do sistema
          </Typography>
        </Box>
      </Box>

      {/* Filtros Globais */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Filtros Globais
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data Inicial"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filtros.data_inicial}
              onChange={(e) => setFiltros(f => ({ ...f, data_inicial: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data Final"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filtros.data_final}
              onChange={(e) => setFiltros(f => ({ ...f, data_final: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              options={clientes}
              value={filtros.cliente}
              onChange={(_, val) => setFiltros(f => ({ ...f, cliente: val }))}
              getOptionLabel={(opt) => opt?.nome || ''}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              renderInput={(params) => <TextField {...params} label="Cliente" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              options={veiculos}
              value={filtros.veiculo}
              onChange={(_, val) => setFiltros(f => ({ ...f, veiculo: val }))}
              getOptionLabel={(opt) => `${opt?.placa} - ${opt?.modelo}` || ''}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              renderInput={(params) => <TextField {...params} label="Veículo" fullWidth />}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={carregarAlugueis} disabled={loading}>
                Aplicar Filtros
              </Button>
              <Button variant="outlined" onClick={limparFiltros}>
                Limpar
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                startIcon={<PdfIcon />}
                onClick={exportarPDF}
                disabled={alugueis.length === 0}
              >
                Exportar PDF
              </Button>
              <Button variant="outlined" color="success" onClick={exportarExcel}>
                Exportar Excel
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Abas */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} variant="fullWidth">
          <Tab icon={<DashboardIcon />} label="Visão Geral" />
          <Tab icon={<MoneyIcon />} label="Financeiro" />
          <Tab icon={<CarIcon />} label="Veículos" />
          <Tab icon={<PeopleIcon />} label="Clientes" />
          <Tab icon={<WarningIcon />} label="Alertas" />
        </Tabs>
      </Paper>

      {/* ABA 1: VISÃO GERAL */}
      {tabIndex === 0 && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <ChartIcon />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Total de Aluguéis</Typography>
                      <Typography variant="h5" fontWeight={700}>{stats.totalAlugueis}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ bgcolor: 'success.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <MoneyIcon />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Receita Total</Typography>
                      <Typography variant="h5" fontWeight={700}>{formatarValor(stats.receitaTotal)}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ bgcolor: 'warning.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <TrendingIcon />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Ticket Médio</Typography>
                      <Typography variant="h5" fontWeight={700}>{formatarValor(stats.ticketMedio)}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ bgcolor: 'info.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <CalendarIcon />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Média de Dias</Typography>
                      <Typography variant="h5" fontWeight={700}>{stats.mediaDias.toFixed(1)} dias</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Evolução Temporal
            </Typography>
            <Box sx={{ height: 300 }}>
              <canvas id="grafico-timeline" />
            </Box>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Últimos Aluguéis
            </Typography>
            <MaterialReactTable
              columns={colunas}
              data={alugueis.slice(0, 10)}
              localization={MRT_Localization_PT_BR}
              enablePagination={false}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              muiTableProps={{ sx: { tableLayout: 'fixed' } }}
            />
          </Paper>
        </>
      )}

      {/* ABA 2: FINANCEIRO */}
      {tabIndex === 1 && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <MoneyIcon color="success" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Receita Bruta</Typography>
                      <Typography variant="h5" fontWeight={700}>{formatarValor(stats.receitaTotal)}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <PaymentIcon color="error" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Descontos</Typography>
                      <Typography variant="h5" fontWeight={700}>{formatarValor(stats.descontosTotal)}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <TrendingIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Receita Líquida</Typography>
                      <Typography variant="h5" fontWeight={700}>{formatarValor(stats.receitaLiquida)}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Formas de Pagamento
                </Typography>
                <Box sx={{ height: 300 }}>
                  <canvas id="grafico-pagamento" />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Resumo Financeiro
                </Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Ticket Médio por Aluguel</Typography>
                    <Typography variant="h6">{formatarValor(stats.ticketMedio)}</Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total de Transações</Typography>
                    <Typography variant="h6">{stats.totalAlugueis}</Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Taxa de Desconto Média</Typography>
                    <Typography variant="h6">
                      {stats.receitaTotal > 0 ? ((stats.descontosTotal / stats.receitaTotal) * 100).toFixed(1) : 0}%
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Detalhamento Financeiro
            </Typography>
            <MaterialReactTable
              columns={colunas}
              data={dadosTabela}
              localization={MRT_Localization_PT_BR}
              enablePagination
              initialState={{ pagination: { pageSize: 25 } }}
            />
          </Paper>
        </>
      )}

      {/* ABA 3: VEÍCULOS */}
      {tabIndex === 2 && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <CarIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Veículos Ativos</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {new Set(alugueis.map(a => a.placa)).size}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <SpeedIcon color="success" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Mais Alugado</Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {analiseVeiculos[0]?.placa || 'N/A'}
                      </Typography>
                      <Typography variant="caption">
                        {analiseVeiculos[0]?.totalAlugueis || 0} aluguéis
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <MoneyIcon color="warning" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Maior Receita</Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {analiseVeiculos[0]?.placa || 'N/A'}
                      </Typography>
                      <Typography variant="caption">
                        {formatarValor(analiseVeiculos[0]?.receita || 0)}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Top 10 Veículos Mais Alugados
            </Typography>
            <Box sx={{ height: 400 }}>
              <canvas id="grafico-veiculos" />
            </Box>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Histórico por Veículo
            </Typography>
            <MaterialReactTable
              columns={colunas}
              data={dadosTabela}
              localization={MRT_Localization_PT_BR}
              enablePagination
              initialState={{ pagination: { pageSize: 25 } }}
            />
          </Paper>
        </>
      )}

      {/* ABA 4: CLIENTES */}
      {tabIndex === 3 && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Total de Clientes</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {new Set(alugueis.map(a => a.nome_cliente)).size}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <TrendingIcon color="success" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Cliente Top</Typography>
                      <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
                        {analiseClientes[0]?.nome.substring(0, 20) || 'N/A'}
                      </Typography>
                      <Typography variant="caption">
                        {analiseClientes[0]?.totalAlugueis || 0} aluguéis
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <MoneyIcon color="warning" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Ticket Médio/Cliente</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatarValor(stats.ticketMedio)}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Top 10 Clientes
            </Typography>
            <Box sx={{ height: 400 }}>
              <canvas id="grafico-clientes" />
            </Box>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Histórico por Cliente
            </Typography>
            <MaterialReactTable
              columns={colunas}
              data={dadosTabela}
              localization={MRT_Localization_PT_BR}
              enablePagination
              initialState={{ pagination: { pageSize: 25 } }}
            />
          </Paper>
        </>
      )}

      {/* ABA 5: ALERTAS */}
      {tabIndex === 4 && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'error.50' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <WarningIcon color="error" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">+30 Dias em Aberto</Typography>
                      <Typography variant="h4" fontWeight={700} color="error.main">
                        {alertas.abertosMais30.length}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'warning.50' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <CalendarIcon color="warning" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Devoluções Atrasadas</Typography>
                      <Typography variant="h4" fontWeight={700} color="warning.main">
                        {alertas.atrasados.length}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'info.50' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <MoneyIcon color="info" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Pagamentos Pendentes</Typography>
                      <Typography variant="h4" fontWeight={700} color="info.main">
                        {alertas.pendentes.length}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {alertas.abertosMais30.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                ⚠️ Atenção! {alertas.abertosMais30.length} aluguel(eis) em aberto há mais de 30 dias!
              </Typography>
              <Typography variant="body2">
                Esses aluguéis precisam de atenção imediata para regularização.
              </Typography>
            </Alert>
          )}

          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Itens que Requerem Atenção
            </Typography>
            <MaterialReactTable
              columns={colunas}
              data={dadosTabela}
              localization={MRT_Localization_PT_BR}
              enablePagination
              initialState={{ pagination: { pageSize: 25 } }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  bgcolor: row.original.dias_atraso > 0 ? '#ffebee' : 
                           new Date(row.original.data_retirada) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) ? '#ffe0e0' : 
                           'inherit'
                }
              })}
            />
          </Paper>
        </>
      )}
    </Container>
  );
}
